import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { consola } from 'consola'
import type { TokenExpirationInfo } from '@entities/order'

// Инициализируем плагины dayjs
dayjs.extend(utc)
dayjs.extend(timezone)

const logger = consola.withTag('token-utils')

/**
 * Константы для работы с токеном
 */
export const TOKEN_CONSTANTS = {
  LIFETIME_MS: 15 * 60 * 1000, // 15 минут в миллисекундах (стандарт для платежных систем)
  UPDATE_INTERVAL_MS: 1000, // Интервал обновления каждую секунду
} as const

/**
 * Вычисляет информацию об истечении токена
 * @param createdAt - дата создания токена в ISO формате (UTC)
 * @returns информация об истечении токена
 */
export function calculateTokenExpiration(createdAt: string): TokenExpirationInfo {
  // Используем dayjs для корректной работы с UTC временем
  const created = dayjs.utc(createdAt)

  // ВАЖНО: Проверяем, является ли дата валидной
  if (!created.isValid()) {
    logger.error('Invalid token creation date', { createdAt })
    // Возвращаем "истекший" токен для безопасности
    return {
      createdAt: dayjs.utc().toISOString(),
      expiresAt: dayjs.utc().toISOString(),
      isExpired: true,
      remainingTime: 0,
    }
  }

  const expires = created.add(TOKEN_CONSTANTS.LIFETIME_MS, 'milliseconds')
  const now = dayjs.utc()
  const remainingTime = expires.diff(now)

  return {
    createdAt: created.toISOString(),
    expiresAt: expires.toISOString(),
    isExpired: remainingTime <= 0,
    remainingTime: Math.max(0, remainingTime),
  }
}

/**
 * Проверяет, истек ли токен по дате создания
 * @param createdAt - дата создания токена
 * @returns true если токен истек
 */
export function isTokenExpired(createdAt: string): boolean {
  const expiration = calculateTokenExpiration(createdAt)
  return expiration.isExpired
}

/**
 * Форматирует оставшееся время до истечения токена в формате MM:SS
 * @param remainingTimeMs - оставшееся время в миллисекундах
 * @returns отформатированная строка времени
 */
export function formatTimeRemaining(remainingTimeMs: number): string {
  if (remainingTimeMs <= 0) return '00:00'
  
  const minutes = Math.floor(remainingTimeMs / 60_000)
  const seconds = Math.floor((remainingTimeMs % 60_000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Получает оставшееся время до истечения токена
 * @param createdAt - дата создания токена
 * @returns оставшееся время в миллисекундах
 */
export function getRemainingTime(createdAt: string): number {
  const expiration = calculateTokenExpiration(createdAt)
  return expiration.remainingTime
}

/**
 * Проверяет, нужно ли показывать предупреждение об истечении токена
 * @param remainingTimeMs - оставшееся время в миллисекундах
 * @param warningThresholdMs - порог предупреждения в миллисекундах (по умолчанию 2 минуты)
 * @returns true если нужно показать предупреждение
 */
export function shouldShowExpirationWarning(
  remainingTimeMs: number, 
  warningThresholdMs: number = 2 * 60 * 1000,
): boolean {
  return remainingTimeMs > 0 && remainingTimeMs <= warningThresholdMs
}